import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  forwardRef,
  inject
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  FormControl
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { calendarlogoComponent } from '../icons/calendar';
import { LocaleService } from 'src/app/services/locale';

@Component({
  selector: 'app-year-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, calendarlogoComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => YearPickerComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-2 relative" #yearPickerContainer>
      <label *ngIf="label" class="text-[#616161]">{{ label }}</label>

      <div class="flex relative">
        <input
          [value]="displayValue"
          (click)="togglePicker()"
          [placeholder]="placeholder"
          readonly
          class="p-3 rounded-lg border w-full border-[#CED4DA] focus:outline-none cursor-pointer"
        />
        <div
          class="absolute right-3 -z-10"
          [ngClass]="{ 'top-[45px]': label, 'top-[12px]': !label }"
        >
          <app-calendar-logo></app-calendar-logo>
        </div>
      </div>

      <div
        *ngIf="showPicker"
        class="absolute top-full left-0 mt-2 z-10 bg-white border border-gray-200 shadow-lg rounded-lg p-4 w-[240px]"
      >
        <div class="flex justify-between items-center mb-2">
          <button (click)="navigateDecade(-10)">&#8592;</button>
          <span class="font-semibold"
            >{{ startDecade }} - {{ startDecade + 9 }}</span
          >
          <button (click)="navigateDecade(10)">&#8594;</button>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <div
            *ngFor="let year of yearRange"
            class="text-center p-2 rounded cursor-pointer transition"
            [class.bg-[#043B22]]="year === value"
            [class.text-white]="year === value"
            [class.hover\\:bg-[#616161]]="year !== value"
            (click)="selectYear(year)"
          >
            {{ year }}
          </div>
        </div>
      </div>
    </div>
  `
})
export class YearPickerComponent implements ControlValueAccessor {
  @ViewChild('yearPickerContainer') containerRef!: ElementRef;
  @Input() label: string = '';
  language = inject(LocaleService).getLanguage();

  @Input() placeholder: string =
    this.language === 'english' ? 'Select year' : " Seleziona l'anno";
  @Output() yearChange = new EventEmitter<number>();

  private _control?: FormControl;
  @Input() set control(control: FormControl | undefined) {
    this._control = control;
    if (control && control.value) {
      this.writeValue(control.value);
    }
  }

  showPicker = false;
  displayValue = '';
  selectedYear = new Date().getFullYear();
  startDecade = this.selectedYear - (this.selectedYear % 10);

  private _value: number | null = null;

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): number | null {
    return this._value;
  }

  set value(val: number | null) {
    this._value = val;
    this.updateDisplayValue();
    this.onChange(val);
    this.onTouched();
    this.yearChange.emit(val!);
  }

  get yearRange(): number[] {
    return Array.from({ length: 10 }, (_, i) => this.startDecade + i);
  }

  togglePicker() {
    this.showPicker = !this.showPicker;
  }

  navigateDecade(delta: number) {
    this.startDecade += delta;
  }

  selectYear(year: number) {
    this.value = year;
    this.showPicker = false;
    if (this._control) {
      this._control.setValue(year);
      this._control.markAsTouched();
    }
    this.yearChange.emit(year);
  }

  updateDisplayValue() {
    this.displayValue = this.value?.toString() ?? '';
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.containerRef?.nativeElement.contains(event.target)) {
      this.showPicker = false;
    }
  }

  writeValue(obj: number | null): void {
    this._value = obj;
    this.updateDisplayValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Optional: add disabled logic
  }
}
